import React, { useEffect, useState } from 'react';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from '../messages';
import {
  CourseAnalysis,
  ScenariosResult,
  SkillSection,
  SubRubricDimension,
  analyzeCourse,
  bulkCreateLtiConfigs,
  generateScenarios,
  getWizardJob,
} from '../data/api';
import EvaluationAnchors from './EvaluationAnchors';

type WizardStep = 'idle' | 'step1_running' | 'step1_done' | 'step2_running' | 'step2_done' | 'saving';

interface AiWizardProps {
  courseId: string;
  onSaved: () => void;
  onCancel: () => void;
}

const AiWizard: React.FC<AiWizardProps> = ({ courseId, onSaved, onCancel }) => {
  const intl = useIntl();

  const [step, setStep] = useState<WizardStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobOperation, setJobOperation] = useState<'analyze' | 'scenarios' | null>(null);
  const [jobLabel, setJobLabel] = useState<string | null>(null);

  // Step 1 state
  const [analysis, setAnalysis] = useState<CourseAnalysis | null>(null);
  const [pendingTopLevelRemoval, setPendingTopLevelRemoval] = useState<number | null>(null);

  // Step 2 state
  const [scenarios, setScenarios] = useState<ScenariosResult | null>(null);
  const [maxAttempts, setMaxAttempts] = useState(3);

  // ── Step 1: analyse course ───────────────────────────────────────────────

  const handleAnalyse = async () => {
    setStep('step1_running');
    setError(null);
    try {
      const job = await analyzeCourse(courseId);
      setJobId(job.job_id);
      setJobOperation('analyze');
      setJobLabel(job.label);
    } catch {
      setError(intl.formatMessage(messages.wizardAnalysisErrorMessage));
      setStep('idle');
    }
  };

  const updateCourseSkill = (val: string) => {
    if (!analysis) { return; }
    setAnalysis({ ...analysis, course_skill_name: val });
  };

  const updateSection = (idx: number, updates: Partial<SkillSection>) => {
    if (!analysis) { return; }
    const sections = analysis.skill_sections.map((s, i) => (i === idx ? { ...s, ...updates } : s));
    setAnalysis({ ...analysis, skill_sections: sections });
  };

  const updateSubDim = (
    secIdx: number,
    dimIdx: number,
    updates: Partial<SubRubricDimension>,
  ) => {
    if (!analysis) { return; }
    const sections = analysis.skill_sections.map((s, i) => {
      if (i !== secIdx) { return s; }
      const updatedSubRubric = s.sub_rubric.map((d, j) => (j === dimIdx ? { ...d, ...updates } : d));
      return { ...s, sub_rubric: updatedSubRubric };
    });
    setAnalysis({ ...analysis, skill_sections: sections });
  };

  const updateTopLevelDim = (dimIdx: number, updates: Partial<SubRubricDimension>) => {
    if (!analysis) { return; }
    const topLevelRubric = analysis.top_level_rubric.map((dimension, index) => (
      index === dimIdx ? { ...dimension, ...updates } : dimension
    ));
    setAnalysis({ ...analysis, top_level_rubric: topLevelRubric });
  };

  const addTopLevelDim = () => {
    if (!analysis) { return; }
    setAnalysis({
      ...analysis,
      top_level_rubric: [
        ...analysis.top_level_rubric,
        {
          name: '',
          weight: 1,
          observable_criteria: [''],
          course_concepts: [{ name: '', definition: '' }],
          source_excerpts: [{ text: '', sequential_id: '', vertical_id: '' }],
        },
      ],
    });
  };

  const removeTopLevelDim = (dimIdx: number) => {
    if (!analysis) { return; }
    setAnalysis({
      ...analysis,
      top_level_rubric: analysis.top_level_rubric.filter((_, index) => index !== dimIdx),
    });
    setPendingTopLevelRemoval(null);
  };

  // ── Step 2: generate scenarios ───────────────────────────────────────────

  const handleGenerate = async () => {
    if (!analysis) { return; }
    setStep('step2_running');
    setError(null);
    try {
      const job = await generateScenarios(analysis);
      setJobId(job.job_id);
      setJobOperation('scenarios');
      setJobLabel(job.label);
    } catch {
      setError(intl.formatMessage(messages.wizardScenariosErrorMessage));
      setStep('step1_done');
    }
  };

  useEffect(() => {
    if (!jobId || !jobOperation) { return undefined; }

    let cancelled = false;
    const pollJob = async () => {
      try {
        const job = await getWizardJob<CourseAnalysis | ScenariosResult>(jobId);
        if (cancelled) { return; }

        setJobLabel(job.label);
        if (job.status === 'ready' && job.result) {
          if (jobOperation === 'analyze') {
            setAnalysis(job.result as CourseAnalysis);
            setStep('step1_done');
          } else {
            setScenarios(job.result as ScenariosResult);
            setStep('step2_done');
          }
          setJobId(null);
          setJobOperation(null);
        } else if (job.status === 'error') {
          setError(jobOperation === 'analyze'
            ? intl.formatMessage(messages.wizardAnalysisErrorMessage)
            : intl.formatMessage(messages.wizardScenariosErrorMessage));
          setStep(jobOperation === 'analyze' ? 'idle' : 'step1_done');
          setJobId(null);
          setJobOperation(null);
        }
      } catch {
        if (cancelled) { return; }
        setError(jobOperation === 'analyze'
          ? intl.formatMessage(messages.wizardAnalysisErrorMessage)
          : intl.formatMessage(messages.wizardScenariosErrorMessage));
        setStep(jobOperation === 'analyze' ? 'idle' : 'step1_done');
        setJobId(null);
        setJobOperation(null);
      }
    };

    pollJob();
    const intervalId = window.setInterval(pollJob, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [intl, jobId, jobOperation]);

  const updateAssessmentScenario = (field: 'scenario_text' | 'response_prompt', val: string) => {
    if (!scenarios) { return; }
    setScenarios({
      ...scenarios,
      assessment_scenario: { ...scenarios.assessment_scenario, [field]: val },
    });
  };

  const updateCheckinScenario = (
    sectionIndex: number,
    field: 'scenario_text' | 'response_prompt',
    val: string,
  ) => {
    if (!scenarios) { return; }
    const updatedCheckins = scenarios.checkin_scenarios.map((cs) => {
      if (cs.section_index !== sectionIndex) { return cs; }
      return { ...cs, scenario: { ...cs.scenario, [field]: val } };
    });
    setScenarios({ ...scenarios, checkin_scenarios: updatedCheckins });
  };

  // ── Step 3: save ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!analysis || !scenarios) { return; }
    setStep('saving');
    setError(null);
    try {
      await bulkCreateLtiConfigs(courseId, analysis, scenarios, maxAttempts);
      onSaved();
    } catch {
      setError(intl.formatMessage(messages.wizardSaveErrorMessage));
      setStep('step2_done');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const isRunning = step === 'step1_running' || step === 'step2_running' || step === 'saving';

  return (
    <div className="card lti-tools-tab__wizard">
      <div className="card-header lti-tools-tab__wizard-header">
        <strong>✨ {intl.formatMessage(messages.wizardHeading)} — {courseId}</strong>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onCancel}
          disabled={isRunning}
        >
          {intl.formatMessage(messages.wizardCancelButton)}
        </button>
      </div>

      <div className="card-body">
        <p className="lti-tools-tab__panel-description lti-tools-tab__panel-description--spacious">{intl.formatMessage(messages.wizardDescription)}</p>

        {error && (
          <div className="alert alert-danger alert-dismissible lti-tools-tab__alert">
            {error}
            <button type="button" className="lti-tools-tab__alert-close" onClick={() => setError(null)} aria-label="Close">×</button>
          </div>
        )}

        {/* ── Step 1 ─────────────────────────────────────────────────────── */}
        <div className="lti-tools-tab__wizard-step">
          <h5 className="lti-tools-tab__step-title">{intl.formatMessage(messages.wizardStep1Heading)}</h5>
          <p className="lti-tools-tab__step-description">{intl.formatMessage(messages.wizardStep1Description)}</p>

          {step === 'idle' && (
            <button type="button" className="btn btn-primary" onClick={handleAnalyse}>
              {intl.formatMessage(messages.wizardAnalyseButton)}
            </button>
          )}

          {step === 'step1_running' && (
            <p className="lti-tools-tab__loading-text">
              <span className="spinner-border lti-tools-tab__spinner" role="status" aria-hidden="true" />
              {jobLabel ?? intl.formatMessage(messages.wizardAnalysingMessage)}
            </p>
          )}

          {(step === 'step1_done' || step === 'step2_running' || step === 'step2_done' || step === 'saving') && analysis && (
            <div>
              <h6 className="lti-tools-tab__result-heading">{intl.formatMessage(messages.wizardStep1ResultHeading)}</h6>

              {/* Overall course skill name */}
              <div className="lti-tools-tab__field lti-tools-tab__field--spacious">
                <label htmlFor="wizard-course-skill" className="form-label lti-tools-tab__field-label">
                  {intl.formatMessage(messages.wizardCourseSkillLabel)}
                </label>
                <input
                  id="wizard-course-skill"
                  className="form-control lti-tools-tab__compact-control lti-tools-tab__course-skill-input"
                  value={analysis.course_skill_name}
                  onChange={(e) => updateCourseSkill(e.target.value)}
                  disabled={step !== 'step1_done'}
                />
                <div className="form-text">{intl.formatMessage(messages.wizardCourseSkillHelp)}</div>
              </div>

              {/* Per-section editors */}
              {analysis.skill_sections.map((sec, si) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={si} className="lti-tools-tab__section-card">
                  <div className="lti-tools-tab__section-card-header">
                    <span className="lti-badge-section">{`Section ${si + 1}:`}</span>
                    <span className="lti-tools-tab__section-name">{sec.section_name}</span>
                  </div>

                  <div className="lti-tools-tab__field">
                    <label
                      htmlFor={`wizard-sec-skill-${si}`}
                      className="form-label lti-tools-tab__field-label"
                    >
                      {intl.formatMessage(messages.wizardSectionSkillLabel)}
                    </label>
                    <input
                      id={`wizard-sec-skill-${si}`}
                      className="form-control lti-tools-tab__compact-control lti-tools-tab__section-skill-input"
                      value={sec.skill_name}
                      onChange={(e) => updateSection(si, { skill_name: e.target.value })}
                      disabled={step !== 'step1_done'}
                    />
                  </div>

                  <p className="form-text lti-tools-tab__placement-hint">
                    💡<strong> {sec.placement_hint}</strong>
                  </p>

                  <p className="lti-tools-tab__subheading">{intl.formatMessage(messages.wizardSubRubricHeading)}</p>
                  {sec.sub_rubric.map((dim, di) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={di} className="lti-tools-tab__rubric-dimension">
                      <input
                        className="form-control lti-tools-tab__compact-control lti-tools-tab__rubric-name-input"
                        value={dim.name}
                        placeholder="Dimension name"
                        onChange={(e) => updateSubDim(si, di, { name: e.target.value })}
                        disabled={step !== 'step1_done'}
                      />
                      <EvaluationAnchors
                        dimension={dim}
                        onChange={(updates) => updateSubDim(si, di, updates)}
                        disabled={step !== 'step1_done'}
                      />
                    </div>
                  ))}
                </div>
              ))}

              <div className="lti-tools-tab__top-level-rubric">
                <p className="lti-tools-tab__subheading">Pre/Post Evaluation Anchors</p>
                {analysis.top_level_rubric.map((dimension, index) => (
                  <div key={dimension.name} className="lti-tools-tab__rubric-dimension">
                    <div className="lti-tools-tab__rubric-dimension-heading">
                      <input
                        className="form-control lti-tools-tab__compact-control"
                        value={dimension.name}
                        aria-label={`Pre/Post rubric dimension ${index + 1} name`}
                        onChange={(event) => updateTopLevelDim(index, { name: event.target.value })}
                        disabled={step !== 'step1_done'}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setPendingTopLevelRemoval(index)}
                        disabled={step !== 'step1_done'}
                      >
                        Remove dimension
                      </button>
                    </div>
                    {pendingTopLevelRemoval === index && (
                      <div className="lti-tools-tab__inline-confirmation" role="status">
                        Remove this rubric dimension and all of its anchors?
                        <button type="button" className="btn btn-sm btn-link" onClick={() => setPendingTopLevelRemoval(null)}>Cancel</button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={() => removeTopLevelDim(index)}>Remove</button>
                      </div>
                    )}
                    <EvaluationAnchors
                      dimension={dimension}
                      onChange={(updates) => updateTopLevelDim(index, updates)}
                      disabled={step !== 'step1_done'}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary lti-tools-tab__add-rubric-dimension"
                  onClick={addTopLevelDim}
                  disabled={step !== 'step1_done'}
                >
                  + Add rubric dimension
                </button>
              </div>

              {step === 'step1_done' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGenerate}
                >
                  {intl.formatMessage(messages.wizardNextButton)}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Step 2 ─────────────────────────────────────────────────────── */}
        {(step === 'step2_running' || step === 'step2_done' || step === 'saving') && (
          <div className="lti-tools-tab__wizard-step">
            <h5 className="lti-tools-tab__step-title">{intl.formatMessage(messages.wizardStep2Heading)}</h5>
            <p className="lti-tools-tab__step-description">{intl.formatMessage(messages.wizardStep2Description)}</p>

            {step === 'step2_running' && (
              <p className="lti-tools-tab__loading-text">
                <span className="spinner-border lti-tools-tab__spinner" role="status" aria-hidden="true" />
                {jobLabel ?? intl.formatMessage(messages.wizardGeneratingMessage)}
              </p>
            )}

            {(step === 'step2_done' || step === 'saving') && scenarios && (
              <div>
                <h6 className="lti-tools-tab__result-heading">{intl.formatMessage(messages.wizardStep2ResultHeading)}</h6>

                {/* Assessment scenario */}
                <div className="lti-tools-tab__section-card lti-tools-tab__section-card--spacious">
                  <p className="lti-tools-tab__subheading">
                    {intl.formatMessage(messages.wizardAssessmentScenarioHeading)}
                  </p>
                  <p className="lti-tools-tab__step-description">
                    {intl.formatMessage(messages.wizardAssessmentScenarioHelp)}
                  </p>
                  <div className="lti-tools-tab__field">
                    <label className="form-label lti-tools-tab__field-label">
                      {intl.formatMessage(messages.wizardScenarioTextLabel)}
                    </label>
                    <textarea
                      className="form-control lti-tools-tab__compact-control"
                      rows={5}
                      value={scenarios.assessment_scenario.scenario_text}
                      onChange={(e) => updateAssessmentScenario('scenario_text', e.target.value)}
                      disabled={step === 'saving'}
                    />
                  </div>
                  <div className="lti-tools-tab__field">
                    <label className="form-label lti-tools-tab__field-label">
                      {intl.formatMessage(messages.wizardResponsePromptLabel)}
                    </label>
                    <input
                      className="form-control lti-tools-tab__compact-control"
                      value={scenarios.assessment_scenario.response_prompt}
                      onChange={(e) => updateAssessmentScenario('response_prompt', e.target.value)}
                      disabled={step === 'saving'}
                    />
                  </div>
                </div>

                {/* Check-in scenarios */}
                <h6 className="lti-tools-tab__result-heading">{intl.formatMessage(messages.wizardCheckinScenariosHeading)}</h6>
                {scenarios.checkin_scenarios.map((cs, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="lti-tools-tab__section-card">
                    <div className="lti-tools-tab__section-card-header lti-tools-tab__section-card-header--centered">
                      <span className="lti-badge-checkin">{`Check-in ${i + 1}:`}</span>
                      <span className="lti-tools-tab__checkin-skill">{cs.skill_name}</span>
                    </div>
                    <div className="lti-tools-tab__field">
                      <label className="form-label lti-tools-tab__field-label">
                        {intl.formatMessage(messages.wizardScenarioTextLabel)}
                      </label>
                      <textarea
                        className="form-control lti-tools-tab__compact-control"
                        rows={8}
                        value={cs.scenario.scenario_text}
                        onChange={(e) => updateCheckinScenario(cs.section_index, 'scenario_text', e.target.value)}
                        disabled={step === 'saving'}
                      />
                    </div>
                    <div className="lti-tools-tab__field">
                      <label className="form-label lti-tools-tab__field-label">
                        {intl.formatMessage(messages.wizardResponsePromptLabel)}
                      </label>
                      <input
                        className="form-control lti-tools-tab__compact-control"
                        value={cs.scenario.response_prompt}
                        onChange={(e) => updateCheckinScenario(cs.section_index, 'response_prompt', e.target.value)}
                        disabled={step === 'saving'}
                      />
                    </div>
                  </div>
                ))}

                {/* Max attempts for check-ins */}
                <div className="lti-tools-tab__field lti-tools-tab__field--spacious lti-tools-tab__wizard-max-attempts-input">
                  <label htmlFor="wizard-max-attempts" className="form-label lti-tools-tab__field-label">
                    {intl.formatMessage(messages.wizardMaxAttemptsLabel)}
                  </label>
                  <input
                    id="wizard-max-attempts"
                    type="number"
                    min={1}
                    max={5}
                    className="form-control lti-tools-tab__compact-control"
                    value={maxAttempts}
                    onChange={(e) => setMaxAttempts(Number(e.target.value))}
                    disabled={step === 'saving'}
                  />
                  <div className="form-text">{intl.formatMessage(messages.wizardMaxAttemptsHelp)}</div>
                </div>

                {step === 'step2_done' && (
                  <div className="lti-tools-tab__button-group">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleSave}
                    >
                      {intl.formatMessage(messages.wizardSaveButton)}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStep('step1_done')}
                    >
                      {intl.formatMessage(messages.wizardBackButton)}
                    </button>
                  </div>
                )}

                {step === 'saving' && (
                  <p className="lti-tools-tab__loading-text">
                    <span className="spinner-border lti-tools-tab__spinner" role="status" aria-hidden="true" />
                    {intl.formatMessage(messages.wizardSavingMessage)}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiWizard;
